import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Flame, TreePine, Ruler } from "lucide-react";
import { IMG, STONES } from "../lib/site";
import { Reveal, Eyebrow, SectionHead, EmberField, GoldButton } from "../components/motion";

const SPECS = [
  { icon: Flame, label: "Burner", value: "65,000 BTU stainless jet burner, adjustable flame" },
  { icon: ShieldCheck, label: "Safety", value: "CSA-compliant propane assembly, certified fittings" },
  { icon: TreePine, label: "Barrel", value: "Authentic decommissioned oak, kiln-stabilized & sealed" },
  { icon: Ruler, label: "Dimensions", value: 'Approx. 42" diameter top · 36" gathered height' },
];

export default function Craftsmanship() {
  return (
    <>
      <section className="relative overflow-hidden pb-20 pt-40 sm:pb-28">
        <EmberField count={14} />
        <div className="animate-ember-pulse absolute left-[15%] top-[25%] h-80 w-80 rounded-full bg-amber-600/10 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal><Eyebrow>Stone & Craft</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
              Craftsmanship still <span className="italic text-gold-gradient">matters.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
              Materials matter. Details matter. In a world of mass production, individuality
              matters more than ever. Here is what goes into every Cozy Ember.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="provenance-section" className="border-t border-amber-900/20 bg-[#0c0a08] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-10 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="animate-ember-pulse absolute -inset-4 rounded-[2rem] bg-amber-700/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-[1.5rem] border border-stone-800 shadow-ember">
              <img src={IMG.cellar} alt="Aged oak barrels resting in a distillery cellar" className="aspect-[4/3] w-full object-cover transition-transform duration-[1600ms] hover:scale-105" />
              <div className="vignette absolute inset-0" />
            </div>
          </Reveal>
          <div>
            <SectionHead
              eyebrow="Barrel Provenance"
              title="A past life in every stave."
              copy="Our barrels begin in Kentucky bourbon rickhouses and Niagara wine cellars — American and French oak, charred, seasoned, and steeped in years of aging. We select each one by hand, stabilize it, and preserve the marks of its history: the distillery stamps, the wine-stained grain, the toasted interior."
            />
            <Reveal delay={0.25}>
              <p className="mt-6 border-l-2 border-amber-500/60 pl-6 font-serif text-lg italic leading-relaxed text-stone-200">
                No two barrels are alike — which is precisely the point.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="stonework-section" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <SectionHead
            eyebrow="The Stonework"
            title="Cut by water, finished by hand."
            copy="Each tabletop is water-jet cut to a perfect round or square, then honed, flamed or polished and sealed against Canadian freeze-thaw seasons. Six architectural surfaces, each chosen to complement the property it will live on."
          />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {STONES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07}>
                <div data-testid={`craft-stone-${s.id}`} className="group rounded-xl border border-stone-800 bg-[#141110] p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-amber-500/40">
                  <span className="stone-swatch block h-16 w-16 rounded-full border border-white/10" style={{ background: s.swatch }} />
                  <h3 className="mt-5 font-serif text-lg text-stone-100">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 overflow-hidden rounded-[1.5rem] border border-stone-800 bg-white shadow-ember">
              <img src={IMG.swatches} alt="The full Cozy Ember stone library" className="w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="engineering-section" className="border-t border-amber-900/20 bg-[#0c0a08] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-10 lg:grid-cols-2">
          <div>
            <SectionHead
              eyebrow="Fire Engineering"
              title="Theatre you can certify."
              copy="Beneath the lava rock sits a stainless steel jet burner on a CSA-compliant propane assembly — a hidden 20 lb tank inside the barrel, an adjustable flame, and fittings built for four-season Canadian use."
            />
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {SPECS.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                  <div data-testid={`spec-${s.label.toLowerCase()}`} className="rounded-xl border border-stone-800 bg-[#141110] p-5">
                    <s.icon size={18} className="text-amber-500" />
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-amber-500/80">{s.label}</p>
                    <p className="mt-2 text-sm leading-relaxed text-stone-300">{s.value}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="animate-ember-pulse absolute -inset-4 rounded-[2rem] bg-amber-600/15 blur-3xl" />
            <div className="relative overflow-hidden rounded-[1.5rem] border border-amber-500/25 shadow-ember-lg">
              <img src={IMG.flame} alt="Flame detail over volcanic lava rock" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 text-center sm:py-28">
        <Reveal>
          <h2 className="mx-auto max-w-2xl px-5 font-serif text-3xl leading-tight text-stone-50 sm:text-4xl">
            Ready to commission <span className="italic text-gold-gradient">yours?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/configurator" className="mt-9 inline-block">
            <GoldButton data-testid="craft-cta-configurator">Enter the Atelier <ArrowRight size={15} /></GoldButton>
          </Link>
        </Reveal>
      </section>
    </>
  );
}
